
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { percentile } from '@/functions';
import LineChart from './LineChart';
import ChartMixin from './ChartMixin.vue';

const PERCENTILE_STEPS = [0, 10, 25, 50, 75, 90, 100] as const;
const BAND_COLOR_0_100 = 'rgba(102, 102, 255, 0.2)';
const BAND_COLOR_10_90 = 'rgba(102, 102, 255, 0.3)';
const BAND_COLOR_25_75 = 'rgba(102, 102, 255, 0.4)';
const MEDIAN_LINE_COLOR = 'rgba(0, 0, 255, 0.6)';

@Component({
  components: {
    LineChart,
  },
})
export default class EnRyuryoBandChart extends Mixins(ChartMixin) {
  private chartOptions = null;

  get rerender() {
    return this['$store'].getters.rerender;
  }

  set rerender(value) {
    this['$store'].commit('setRerender', value);
  }

  get gridPitch() {
    return this['$store'].getters.enGridPitch;
  }

  get yskSpan() {
    return this['$store'].getters.enYskSpan;
  }

  get maxQScale() {
    return this['$store'].getters.maxQScale === '自動' ? this['$store'].getters.maxQAutoScale : this['$store'].getters.maxQScale;
  }

  get targetTime() {
    return this['$store'].getters.enTargetTime;
  }

  get autoQStep() {
    const { stationsMap } = this['$store'].getters;
    return stationsMap[this.stationId].scales.autoQStep;
  }

  mounted() {
    this.updateResult();
  }

  getOneFromResult(result, key) {
    return result.map((obj) => {
      const keyValue = obj[key] ?? null;
      return keyValue <= -999 ? null : keyValue;
    });
  }

  @Watch('tab')
  @Watch('yskSpan')
  @Watch('result')
  @Watch('rerender')
  public async updateResult() {
    if (!this.result || this.result.length === 0) return;

    const { type } = this['$store'].getters;
    const key = Number(type) === 7 ? 'qinAll' : 'flow';
    const upperLabel = Number(type) === 7 ? '上位流入量' : '上位流量';
    const middleLabel = Number(type) === 7 ? '中位流入量' : '中位流量';
    const bottomLabel = Number(type) === 7 ? '下位流入量' : '下位流量';

    const numTimestamps = this.result[0].length;

    const percentileArrays: Record<number, (number | null)[]> = {
      0: [], 10: [], 25: [], 50: [], 75: [], 90: [], 100: [],
    };
    for (let timeIndex = 0; timeIndex < numTimestamps; timeIndex += 1) {
      const sortedValues = this.result
        .map((member) => {
          const v = member[timeIndex]?.[key] ?? null;
          return v == null || v <= -999 ? null : v;
        })
        .filter((v): v is number => v != null)
        .sort((a, b) => a - b);
      PERCENTILE_STEPS.forEach((p) => {
        percentileArrays[p].push(sortedValues.length > 0 ? percentile(sortedValues, p) : null);
      });
    }

    const spanLen = 7 + this.yskSpan;
    const [p0, p10, p25, p50, p75, p90, p100] = PERCENTILE_STEPS.map((p) => percentileArrays[p].slice(0, spanLen));

    const top = this.getOneFromResult(this.result[0], key).slice(0, spanLen);
    const middle = this.getOneFromResult(this.result[Math.floor(this.result.length / 2)], key).slice(0, spanLen);
    const bottom = this.getOneFromResult(this.result[this.result.length - 1], key).slice(0, spanLen);

    const validP100 = p100.filter((v): v is number => v != null);
    const maxNum = validP100.length > 0 ? Math.max(...validP100) : 0;
    this['$store'].commit('setMaxQAutoScale', Math.ceil(maxNum / this.autoQStep) * this.autoQStep * 2);

    const dataTimeArray = this.result[0].map((obj) => obj.datetime);
    this.chartData = {
      labels: dataTimeArray.slice(0, spanLen),
      datasets: [
        // 0-100% 帯 (P100 → P0) — 凡例はサイドバーで表示
        {
          label: 'P100', data: p100, fill: false, pointRadius: 0, borderWidth: 0, borderColor: 'transparent', notShowLable: true, hideInTooltip: true,
        },
        {
          label: '0-100%', data: p0, fill: '-1', pointRadius: 0, borderWidth: 0, borderColor: 'transparent', backgroundColor: BAND_COLOR_0_100, notShowLable: true, hideInTooltip: true,
        },
        // 10-90% 帯 (P90 → P10)
        {
          label: 'P90', data: p90, fill: false, pointRadius: 0, borderWidth: 0, borderColor: 'transparent', notShowLable: true, hideInTooltip: true,
        },
        {
          label: '10-90%', data: p10, fill: '-1', pointRadius: 0, borderWidth: 0, borderColor: 'transparent', backgroundColor: BAND_COLOR_10_90, notShowLable: true, hideInTooltip: true,
        },
        // 25-75% 帯 (P75 → P25)
        {
          label: 'P75', data: p75, fill: false, pointRadius: 0, borderWidth: 0, borderColor: 'transparent', notShowLable: true, hideInTooltip: true,
        },
        {
          label: '25-75%', data: p25, fill: '-1', pointRadius: 0, borderWidth: 0, borderColor: 'transparent', backgroundColor: BAND_COLOR_25_75, notShowLable: true, hideInTooltip: true,
        },
        // 中央値 (P50 ライン)
        {
          label: '中央値', data: p50, fill: false, pointRadius: 0, borderWidth: 1, borderColor: MEDIAN_LINE_COLOR, notShowLable: true,
        },
        // 上中下位（既存の3本ライン、凡例に表示）
        {
          label: upperLabel, data: top, fill: false, pointRadius: 0, borderWidth: 2, borderColor: '#f00', style: 'border-top-color: #f00;',
        },
        {
          label: middleLabel, data: middle, fill: false, pointRadius: 0, borderWidth: 2, borderColor: '#00f', style: 'border-top-color: #00f;',
        },
        {
          label: bottomLabel, data: bottom, fill: false, pointRadius: 0, borderWidth: 2, borderColor: '#080', style: 'border-top-color: #080;',
        },
      ],
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        filter: (tooltipItem, chartData) => !chartData.datasets[tooltipItem.datasetIndex].hideInTooltip,
        callbacks: {
          label: (tooltipItem, obj) => `${obj.datasets[tooltipItem.datasetIndex].label}:${Number(tooltipItem.value).toFixed(2)}`,
        },
      },
      annotation: {
        annotations: [{
          type: 'line',
          scaleID: 'x-axis-0',
          value: this.targetTime,
          borderWidth: 2,
          borderColor: 'gray',
        }],
      },
      legend: {
        labels: {
          filter: () => false,
        },
      },
      scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: { hour: 'HH:mm' },
              stepSize: this.gridPitch,
            },
            ticks: { fontSize: 14, fontColor: 'black' },
            gridLines: { color: '#999' },
          },
          {
            type: 'time',
            time: {
              unit: 'day',
              displayFormats: { day: 'MM/DD' },
            },
            gridLines: {
              drawTicks: false,
              drawBorder: false,
              drawOnChartArea: false,
              zeroLineColor: 'rgba(0, 0, 0, 0.1)',
              color: '#999',
            },
            ticks: { fontSize: 14, fontColor: 'black' },
          },
        ],
        yAxes: [{
          afterFit(scaleInstance) {
            // eslint-disable-next-line no-param-reassign
            scaleInstance.width = 40;
          },
          gridLines: { color: '#999' },
          ticks: {
            maxTicksLimit: 8,
            min: 0,
            max: this.maxQScale,
            fontSize: 14,
            fontColor: 'black',
          },
        },
        {
          position: 'right',
          display: true,
          gridLines: { color: '#999' },
          ticks: {
            callback() { return ''; },
            fontSize: 14,
            fontColor: 'black',
          },
        },
        ],
      },
    };
  }
}
